import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Project, Activity, AppUser } from '../types';
import { getDemoSessionToken } from '../utils/demoSession';

// Explicit, not inferred — every component that needs to know "is this visitor a real
// user" reads this instead of checking indirect signals (does a project exist, is
// userEmail set, etc). Only two states: anything past the App.tsx auth gate (Landing/
// Login) is either a demo session or a real authenticated one, never neither.
export type UserMode = 'demo' | 'authenticated';

interface AppContextType {
  userMode: UserMode;
  user: AppUser | null;
  setUser: (user: AppUser | null) => void;
  projects: Project[];
  setProjects: (projects: Project[]) => void;
  selectedProject: Project | null;
  setSelectedProject: (project: Project | null) => void;
  activities: Activity[];
  setActivities: (activities: Activity[]) => void;
  darkMode: boolean;
  toggleDarkMode: () => void;
  userDataLoading: boolean;
  setUserDataLoading: (loading: boolean) => void;
  userDataError: string | null;
  setUserDataError: (error: string | null) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Computed once at mount, same as App.tsx's IS_DEMO — entering/exiting demo mode is
  // always a full page navigation in this app, never a soft SPA transition, so this
  // never needs to change mid-session.
  const [userMode] = useState<UserMode>(() => (getDemoSessionToken() ? 'demo' : 'authenticated'));
  const [user, setUser] = useState<AppUser | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    return localStorage.getItem('buildahead-dark') === 'true';
  });
  const [userDataLoading, setUserDataLoading] = useState(false);
  const [userDataError, setUserDataError] = useState<string | null>(null);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('buildahead-dark', String(darkMode));
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode(prev => !prev);

  return (
    <AppContext.Provider
      value={{
        userMode,
        user, setUser,
        projects, setProjects,
        selectedProject, setSelectedProject,
        activities, setActivities,
        darkMode, toggleDarkMode,
        userDataLoading, setUserDataLoading,
        userDataError, setUserDataError,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = (): AppContextType => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
};
